import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { formatNumber, cn } from '@/lib/utils';

interface KpiCardProps {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: { value: number; label: string };
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
  loading?: boolean;
}

const TONE = {
  primary: { bg: 'bg-primary-50',   fg: 'text-primary-700',   ring: 'ring-primary-100' },
  success: { bg: 'bg-emerald-50',   fg: 'text-emerald-700',   ring: 'ring-emerald-100' },
  warning: { bg: 'bg-amber-50',     fg: 'text-amber-700',     ring: 'ring-amber-100' },
  danger:  { bg: 'bg-red-50',       fg: 'text-red-700',       ring: 'ring-red-100' },
  neutral: { bg: 'bg-slate-100',    fg: 'text-slate-700',     ring: 'ring-slate-200' },
};

export function KpiCard({ label, value, icon: Icon, trend, tone = 'primary', loading }: KpiCardProps) {
  const t = TONE[tone];

  return (
    <Card className="p-5 relative overflow-hidden group">
      {/* Halo decorativo */}
      <div
        className={cn(
          'absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-50 transition-transform duration-500 group-hover:scale-110',
          t.bg,
        )}
        aria-hidden
      />
      <div className="relative space-y-3">
        <div className="flex items-start justify-between">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
          <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center ring-1', t.bg, t.fg, t.ring)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>

        <div>
          {loading ? (
            <div className="skeleton h-8 w-24 rounded" />
          ) : (
            <p className="text-3xl font-bold tracking-tight tabular text-foreground">
              {typeof value === 'number' ? formatNumber(value) : value}
            </p>
          )}
        </div>

        {trend && !loading && (
          <div className="flex items-center gap-1 text-xs">
            {trend.value >= 0 ? (
              <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium">
                <ArrowUpRight className="h-3 w-3" />
                {trend.value}%
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-red-600 font-medium">
                <ArrowDownRight className="h-3 w-3" />
                {Math.abs(trend.value)}%
              </span>
            )}
            <span className="text-muted-foreground">{trend.label}</span>
          </div>
        )}
      </div>
    </Card>
  );
}
